import {
  Banner,
  Box,
  FlexBox,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { useCustomForms } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/useCustomForms';
import {
  HookFormSelectionListSingle,
  HookFormSwitch,
} from 'components/hookForm';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { MessengerExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { FlowMessengerNodeCustomForm } from '../../../flow.types';

import { ArchivedFormWarning } from './archivedFormWarning';

type FormValues = {
  formId: string;
  isContactForm: boolean;
  contactFormEmailId?: string;
};

type Props = FlowManagerEditModalComponentT<
  FlowMessengerNodeCustomForm,
  MessengerExtraData
>;

export function MessengerCustomFormNodeModal({
  replaceNode,
  triggerClose,
  node,
  extraData: { forms },
}: Props): JSX.Element {
  const { hasCustomFormsV2Feature } = useFeatureFlag([
    'has-custom-forms-v2-feature',
  ]);

  const { getSingleForm } = useCustomForms();

  const fromEmails = useAppSelector(
    ({ composeEmail }) => composeEmail.fromEmails,
  );

  const [formCanBeConvertedToContactForm, setFormCanBeConvertedToContactForm] =
    useState(false);

  const currentNodeFormId = node.payload?.formId;

  const currentNodeFormTitle =
    forms.find(({ id }) => id === currentNodeFormId)?.title ?? 'Unknown form';

  const isSelectedFormArchived =
    currentNodeFormId &&
    forms.length > 0 &&
    forms.find(({ id }) => id === currentNodeFormId) === undefined;

  const getInitialFormValues = (): Partial<FormValues> => {
    if (!node.payload) {
      return {};
    }

    if ('contactForm' in node.payload) {
      return {
        formId: currentNodeFormId,
        isContactForm: true,
        contactFormEmailId: node.payload.contactForm.toEmail,
      };
    }

    return {
      formId: currentNodeFormId,
      isContactForm: false,
    };
  };

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit,
    setValue,
  } = useForm<FormValues>({
    defaultValues: getInitialFormValues(),
    mode: 'all',
  });

  const selectedFormId = useWatch({ control, name: 'formId' });
  const isContactForm = useWatch({ control, name: 'isContactForm' });

  const checkIfFormHasSingleRequiredEmailField = useCallback(
    async (formId: string) => {
      const result = await getSingleForm(formId);

      if (result.ok) {
        const { value: selectedForm } = result;

        // check if the form has a single required email field
        const formHasExactlyOneRequiredEmailField =
          selectedForm.fields.filter(
            ({ type, required }) => type === 'email' && required === true,
          ).length === 1;

        return { formHasExactlyOneRequiredEmailField };
      }

      return { formHasExactlyOneRequiredEmailField: false };
    },
    [getSingleForm],
  );

  /**
   * Check if the selected form can be converted to a contact form. A form
   * without any email fields cannot be converted to a contact form. Because we
   * need an email field to send an email to the customer.
   */
  useEffect(() => {
    if (!selectedFormId) {
      setFormCanBeConvertedToContactForm(false);
      return;
    }

    checkIfFormHasSingleRequiredEmailField(selectedFormId)
      .then(({ formHasExactlyOneRequiredEmailField }) => {
        setFormCanBeConvertedToContactForm(formHasExactlyOneRequiredEmailField);
      })
      .catch(() => {
        setFormCanBeConvertedToContactForm(false);
      });
  }, [checkIfFormHasSingleRequiredEmailField, selectedFormId]);

  // remove the contact-form-related fields when the contact-form switch is disabled
  useEffect(() => {
    if (isContactForm === false) {
      // rest the optional fields
      setValue('contactFormEmailId', undefined);
    }
  }, [isContactForm, setValue]);

  const constructNodePayload = (formValues: FormValues) => {
    if (formValues.isContactForm) {
      // Since a contact node is a terminal node, we don't need to set the nextNodeId.
      return {
        formId: formValues.formId,
        contactForm: {
          toEmail: formValues.contactFormEmailId!,
        },
      };
    }

    return {
      formId: formValues.formId,
      nextNodeId:
        node.payload && 'nextNodeId' in node.payload
          ? node.payload?.nextNodeId
          : uuid(),
    };
  };

  const handleSaveClick = (values: FormValues) => {
    const formTitle =
      forms?.find(({ id }) => id === values.formId)?.title || '';

    replaceNode({
      ...node,
      childIds: [],
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.MESSENGER_FORM,
      overlay: [],
      payload: constructNodePayload(values),
      text: formTitle,
    });

    triggerClose();
  };

  return (
    <Box width="550px">
      <form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalHead title="Forms" onClose={triggerClose}>
          Once the customer fills out the form, all of their information will be
          saved within the conversation.
        </ModalHead>

        <ModalBody>
          <FlexBox flexDirection="column" alignItems="center" gap="400">
            {isSelectedFormArchived && (
              <ArchivedFormWarning formTitle={currentNodeFormTitle} />
            )}
            <FormGrid>
              <FormField>
                <HookFormSelectionListSingle
                  name="formId"
                  label="Form"
                  rules={{
                    required: 'Please select a form',
                    validate: async (value) => {
                      if (!value) {
                        return 'Please select a form';
                      }

                      const form = forms?.find(({ id }) => id === value);

                      if (!form) {
                        return 'Please select a valid form';
                      }

                      if (form.hasAttributes === false) {
                        return 'The selected form is empty. To use it, please edit and add fields.';
                      }

                      if (isContactForm) {
                        const { formHasExactlyOneRequiredEmailField } =
                          await checkIfFormHasSingleRequiredEmailField(value);

                        if (!formHasExactlyOneRequiredEmailField) {
                          return 'The form must have exactly one required email field to be used as a contact form.';
                        }
                      }

                      return true;
                    },
                  }}
                  disabled={isSubmitting}
                  control={control}
                  componentProps={{
                    id: 'formId',
                    placeholder: 'Select a form',
                    getItemTitleByValue: (value: string | null | undefined) =>
                      forms?.find(({ id }) => id === value)?.title ||
                      'Unknown form',
                  }}
                >
                  {forms?.map(({ id, title }) => {
                    return (
                      <SelectionList.Item key={id} value={id}>
                        {title}
                      </SelectionList.Item>
                    );
                  })}
                </HookFormSelectionListSingle>

                {hasCustomFormsV2Feature && (
                  <FlexBox flexDirection="column" gap="300" margin="100 0">
                    {!formCanBeConvertedToContactForm && (
                      <Banner
                        variant="announcement"
                        description={
                          <Text.Paragraph variant="4">
                            Only forms with a{' '}
                            <strong>single required email</strong> field can be
                            converted to contact forms.
                          </Text.Paragraph>
                        }
                      />
                    )}

                    <HookFormSwitch
                      control={control}
                      label="Enable contact form"
                      name="isContactForm"
                      disabled={!formCanBeConvertedToContactForm}
                      componentProps={{
                        disabled: !formCanBeConvertedToContactForm,
                      }}
                    />

                    {isContactForm && (
                      <FlexBox gap="300" flexDirection="column">
                        <Text.Paragraph variant="2" subdued>
                          This form will become a terminal node and will create
                          an email conversation.
                        </Text.Paragraph>

                        <div>
                          <HookFormSelectionListSingle
                            shouldUnregister={true}
                            name="contactFormEmailId"
                            label="Email"
                            disabled={isSubmitting}
                            control={control}
                            rules={{
                              required: {
                                value: isContactForm,
                                message: 'Please select an email',
                              },
                            }}
                            componentProps={{
                              id: 'contactFormEmailId',
                              getItemTitleByValue: (value) =>
                                fromEmails?.find(
                                  ({ email_address }) =>
                                    email_address === value,
                                )?.email_address || 'Unknown email',
                            }}
                          >
                            {fromEmails?.map(({ email_address }) => {
                              return (
                                <SelectionList.Item
                                  key={email_address}
                                  value={email_address}
                                >
                                  {email_address}
                                </SelectionList.Item>
                              );
                            })}
                          </HookFormSelectionListSingle>
                        </div>
                      </FlexBox>
                    )}
                  </FlexBox>
                )}
              </FormField>
            </FormGrid>
          </FlexBox>
        </ModalBody>

        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName="Save"
          cancelOnClick={triggerClose}
          confirmProps={{
            disabled: !isValid,
            isLoading: isSubmitting,
            loadingIndicator: 'Saving...',
          }}
        />
      </form>
    </Box>
  );
}
